'use client';

import { getAPIToken } from '@instamenu/context-firebase';
import { captureClientException } from '@instamenu/posthog/client';
import { makeAutoObservable, runInAction } from 'mobx';
import { apiClient } from '@instamenu/context-api';
import * as q from './queries.generated';
import type { Thread } from './thread';

const titleInstructions = `Summarize the following user message into a short label (max 6 words) for a conversation list. Respond with only the title, no other text.`;

const refinedTitleInstructions = `Provide a short label (max 6 words) for the preceding conversation. The label will be used to identify this conversation in a list. Respond with only the title, no other text.`;

type SavedPromptEntry = {
  id: string;
  prompt: string;
  title: string;
  position: number;
};

class Threads {
  activeThreadKey: string | null = null;

  data: {
    key: string;
    thread?: Thread;
    title: string;
    id: string;
    createdAt: number;
    conversationId: string;
    pending?: boolean;
    titleManuallyEdited?: boolean;
  }[] = [];

  savedPrompts: SavedPromptEntry[] = [];
  savedPromptsLoaded = false;
  savedPromptsLoading = false;

  private savedPromptTempId = 0;
  private savedPromptsRequest: Promise<void> | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  async createSavedPrompt(prompt: string) {
    const title = prompt.slice(0, 50);
    const tempId =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? `temp:${crypto.randomUUID()}`
        : `temp:${this.savedPromptTempId++}`;

    // Optimistic update — position after last item
    const lastPos =
      this.savedPrompts.length > 0 ? Math.max(...this.savedPrompts.map((p) => p.position)) : -1;
    const position = lastPos + 1;
    this.savedPrompts.push({ id: tempId, prompt, title, position });

    try {
      const res = await apiClient()
        .mutation<
          q.CreateSavedPromptMutation,
          q.CreateSavedPromptMutationVariables
        >(q.CreateSavedPromptDocument, { input: { prompt, title } })
        .toPromise();

      const mutationResult = res.data?.createSavedPrompt;
      const saved = mutationResult?.savedPrompt;
      const hasErrors = (mutationResult?.errors?.length ?? 0) > 0 || Boolean(res.error);

      runInAction(() => {
        if (!saved || hasErrors) {
          this.savedPrompts = this.savedPrompts.filter((p) => p.id !== tempId);
          return;
        }

        const entry = this.savedPrompts.find((p) => p.id === tempId);
        if (!entry) return;
        entry.id = saved.id;
        entry.prompt = saved.prompt;
        entry.title = saved.title;
        entry.position = saved.position ?? entry.position;
      });
    } catch {
      runInAction(() => {
        this.savedPrompts = this.savedPrompts.filter((p) => p.id !== tempId);
      });
    }
  }

  async moveSavedPrompt(id: string, newIndex: number) {
    const sorted = [...this.savedPrompts].sort((a, b) => a.position - b.position);
    const others = sorted.filter((p) => p.id !== id);

    // Calculate midpoint position between neighbors
    let position: number;
    if (newIndex <= 0) {
      position = (others[0]?.position ?? 1) - 1;
    } else if (newIndex >= others.length) {
      position = (others[others.length - 1]?.position ?? 0) + 1;
    } else {
      position = (others[newIndex - 1].position + others[newIndex].position) / 2;
    }

    // Optimistic update
    const entry = this.savedPrompts.find((p) => p.id === id);
    if (entry) entry.position = position;

    await apiClient().mutation(q.EditSavedPromptDocument, { input: { id, position } }).toPromise();
  }

  async removeSavedPrompt(id: string) {
    const index = this.savedPrompts.findIndex((p) => p.id === id);
    const removedEntry = index === -1 ? null : this.savedPrompts[index];

    runInAction(() => {
      this.savedPrompts = this.savedPrompts.filter((p) => p.id !== id);
    });

    try {
      const res = await apiClient()
        .mutation(q.EditSavedPromptDocument, { input: { id, discard: true } })
        .toPromise();
      const hasErrors = (res.data?.editSavedPrompt?.errors?.length ?? 0) > 0 || Boolean(res.error);

      if (!hasErrors || !removedEntry || index === -1) return;

      runInAction(() => {
        this.savedPrompts.splice(index, 0, removedEntry);
      });
    } catch (error) {
      if (removedEntry && index !== -1) {
        runInAction(() => {
          this.savedPrompts.splice(index, 0, removedEntry);
        });
      }
      throw error;
    }
  }

  async fetch() {
    return apiClient()
      .query<q.ThreadsQuery, q.ThreadsQueryVariables>(q.ThreadsDocument, {})
      .toPromise()
      .then((res) => {
        if (!res.data?.assistantThreads) throw new Error('API ERROR');
        const existingIds = new Set(this.data.map((d) => d.id));
        const newEntries = res.data.assistantThreads
          .filter((t) => !existingIds.has(t.id))
          .map((t) => ({
            ...t,
            key: t.id,
            createdAt: Date.parse(t.createdAt),
            titleManuallyEdited: false,
          }));
        if (newEntries.length > 0) {
          runInAction(() => {
            this.data.push(...newEntries);
          });
        }
      });
  }

  async fetchSavedPrompts() {
    if (this.savedPromptsRequest) return this.savedPromptsRequest;

    const request = apiClient()
      .query<q.SavedPromptsQuery, q.SavedPromptsQueryVariables>(q.SavedPromptsDocument, {})
      .toPromise()
      .then((res) => {
        if (!res.data?.savedPrompts) throw new Error('API ERROR');
        runInAction(() => {
          this.savedPrompts = res.data!.savedPrompts.map((p, i) => ({
            id: p.id,
            prompt: p.prompt,
            title: p.title,
            position: p.position ?? i,
          }));
        });
      })
      .finally(() => {
        runInAction(() => {
          this.savedPromptsLoading = false;
          this.savedPromptsLoaded = true;
          this.savedPromptsRequest = null;
        });
      });

    runInAction(() => {
      this.savedPromptsLoading = true;
      this.savedPromptsRequest = request;
    });

    return request;
  }

  private generateTitle(input: string): Promise<string | undefined> {
    return Promise.resolve(getAPIToken())
      .then((token) =>
        fetch('/api/ai/response', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ model: 'gpt-5.4-nano', reasoning: { effort: 'none' }, input }),
        }),
      )
      .then((res) => (res.ok ? res.json() : undefined))
      .then((res) => res?.output_text as string | undefined)
      .catch((error) => {
        captureClientException(error, {
          source: 'ai-ui.threads.generate-title',
          properties: {
            inputLength: input.length,
          },
        });
        return undefined;
      });
  }

  private fetchTitleFromUserMessage(thread: Thread): Promise<string | undefined> {
    const firstUserMsg = thread.history.find(
      (item) => item.type === 'message' && item.role === 'user' && item.content.type === 'text',
    );
    const userText =
      firstUserMsg?.type === 'message' && firstUserMsg.content.type === 'text'
        ? firstUserMsg.content.text
        : '';
    if (!userText) return Promise.resolve(undefined);
    return this.generateTitle(`${titleInstructions}\n\nUser message: ${userText}`);
  }

  private fetchRefinedTitle(thread: Thread): Promise<string | undefined> {
    const transcript = thread.history
      .filter((item) => item.type === 'message' && item.content.type === 'text')
      .map((item) => {
        const role = item.role === 'user' ? 'User' : 'Assistant';
        const text =
          item.type === 'message' && item.content.type === 'text' ? item.content.text : '';
        return `${role}: ${text.slice(0, 300)}`;
      })
      .join('\n');
    if (!transcript) return Promise.resolve(undefined);
    return this.generateTitle(`${refinedTitleInstructions}\n\nConversation:\n${transcript}`);
  }

  setActive(threadId: string | null) {
    this.activeThreadKey = threadId;
  }

  registerPending(thread: Thread) {
    if (thread.id) return;
    const alreadyPending = this.data.some((d) => d.pending && d.thread === thread);
    if (alreadyPending) return;
    const stableKey = `pending:${Date.now()}`;
    runInAction(() => {
      this.activeThreadKey = stableKey;
      this.data.push({
        key: stableKey,
        title: thread.options.pendingTitle ?? 'New chat',
        id: stableKey,
        createdAt: Date.now(),
        conversationId: '',
        pending: true,
        titleManuallyEdited: false,
        thread,
      });
    });

    // Start title generation immediately — don't wait for the AI response
    this.fetchTitleFromUserMessage(thread).then((title) => {
      if (title) thread.conversationTitle = title;
      runInAction(() => {
        const entry = this.data.find((d) => d.thread === thread);
        if (entry) {
          if (title && !entry.titleManuallyEdited) {
            entry.title = title;
          }
          entry.pending = true;
          // If add() already ran and assigned a real DB id, persist the title
          if (title && thread.id && !entry.titleManuallyEdited) {
            this.update(thread.id, { title }).catch((error) => {
              captureClientException(error, {
                source: 'ai-ui.threads.persist-pending-title',
                properties: {
                  threadId: thread.id,
                  title,
                },
              });
            });
          }
        }
      });
    });
  }

  async add(thread: Thread) {
    if (thread.id || !thread.conversationId) return;

    const existingEntry = this.data.find((d) => d.thread === thread);
    if (!existingEntry) return;

    const restaurantId = thread.context['restaurantId'] as string;
    if (!restaurantId) return;

    // Create the DB record immediately with a placeholder title so a refresh won't lose the chat
    const placeholderTitle = thread.options.pendingTitle ?? 'New chat';
    const createRes = await apiClient()
      .mutation<q.CreateThreadMutation, q.CreateThreadMutationVariables>(q.CreateThreadDocument, {
        input: {
          conversationId: thread.conversationId,
          title: placeholderTitle,
          restaurantId,
        },
      })
      .toPromise();

    const resThread = createRes.data?.createAssistantThread?.assistantThread;
    if (!resThread) throw new Error('API ERROR');

    // Title was already kicked off in registerPending — use it if ready, otherwise keep current title
    const initialTitle = thread.conversationTitle;

    thread.id = resThread.id;
    runInAction(() => {
      const entry = this.data.find((d) => d.thread === thread);
      if (entry) {
        if (this.activeThreadKey === entry.key) {
          this.activeThreadKey = resThread.id;
        }
        entry.id = resThread.id;
        entry.key = resThread.id;
        entry.conversationId = resThread.conversationId;
        entry.pending = true;
        if (initialTitle && !entry.titleManuallyEdited) {
          entry.title = initialTitle;
        }
      }
    });

    // Persist the initial title once the thread has a real id, even while the refined title is pending.
    if (initialTitle && !existingEntry.titleManuallyEdited) {
      this.update(resThread.id, { title: initialTitle }).catch((error) => {
        captureClientException(error, {
          source: 'ai-ui.threads.persist-initial-title',
          properties: {
            threadId: resThread.id,
            title: initialTitle,
          },
        });
      });
    }

    // Second pass: refine the title using the full conversation (user + assistant messages)
    this.fetchRefinedTitle(thread)
      .then((refinedTitle) => {
        if (refinedTitle) {
          thread.conversationTitle = refinedTitle;
          runInAction(() => {
            const entry = this.data.find((d) => d.id === resThread.id);
            if (entry && !entry.titleManuallyEdited) {
              entry.title = refinedTitle;
            }
          });
          const entry = this.data.find((d) => d.id === resThread.id);
          if (!entry?.titleManuallyEdited) {
            this.update(resThread.id, { title: refinedTitle }).catch((error) => {
              captureClientException(error, {
                source: 'ai-ui.threads.persist-refined-title',
                properties: {
                  threadId: resThread.id,
                  title: refinedTitle,
                },
              });
            });
          }
        }
      })
      .catch((error) => {
        captureClientException(error, {
          source: 'ai-ui.threads.fetch-refined-title',
          properties: {
            threadId: resThread.id,
          },
        });
      })
      .finally(() => {
        runInAction(() => {
          const entry = this.data.find((d) => d.id === resThread.id);
          if (entry) {
            entry.pending = false;
          }
        });
      });
  }

  async update(threadId: string, updates: Partial<q.EditThreadMutationVariables['input']>) {
    return apiClient()
      .mutation<q.EditThreadMutation, q.EditThreadMutationVariables>(q.EditThreadDocument, {
        input: { id: threadId, ...updates },
      })
      .toPromise()
      .then((res) => {
        if (!res.data?.editAssistantThread?.assistantThread) throw new Error('API ERROR');
      });
  }

  async rename(threadId: string, title: string) {
    runInAction(() => {
      const entry = this.data.find((d) => d.id === threadId);
      if (entry) {
        entry.title = title;
        entry.titleManuallyEdited = true;
        entry.thread && (entry.thread.conversationTitle = title);
      }
    });
    await this.update(threadId, { title });
  }

  async discard(threadId: string) {
    await this.update(threadId, { discard: true });
    runInAction(() => {
      this.data = this.data.filter((thread) => thread.id !== threadId);
    });
  }
}

export const threads = new Threads();
